// packages/ui/src/components/kanban/CardDetailModal.tsx
// Full card detail modal with inline editing, status dropdown, and execution timeline

import { useState, useEffect, useCallback, useRef } from 'react'
import type { DashboardCard, AuditEvent } from '../../api/client'

const PRIORITY_COLORS: Record<DashboardCard['priority'], string> = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
}

const STATUS_LABELS: Record<string, string> = {
  inbox: 'Inbox',
  research: 'Research',
  planned: 'Planned',
  approved: 'Approved',
  executing: 'In Progress',
  verifying: 'Needs Review',
  done: 'Done',
  failed: 'Failed',
  blocked: 'Blocked',
}

const SOURCE_LABELS: Record<string, string> = {
  chat: 'Chat',
  cron: 'Scheduled',
  webhook: 'Webhook',
  manual: 'Manual',
  discovery: 'Discovery',
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export interface CardDetailModalProps {
  card: DashboardCard & { events?: AuditEvent[] }
  open: boolean
  onClose?: () => void
  onUpdate?: (fields: Partial<Pick<DashboardCard, 'description' | 'priority' | 'title'>>) => void
  onStatusChange?: (newStatus: string) => void
}

export function CardDetailModal({
  card,
  open,
  onClose,
  onUpdate,
  onStatusChange,
}: CardDetailModalProps) {
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [editedDescription, setEditedDescription] = useState(card.description)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Sync edited description when card changes
  useEffect(() => {
    setEditedDescription(card.description)
    setIsEditingDescription(false)
  }, [card.description])

  // Close on escape
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  // Focus textarea when editing starts
  useEffect(() => {
    if (isEditingDescription && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [isEditingDescription])

  const handleSaveDescription = useCallback(() => {
    if (editedDescription !== card.description) {
      onUpdate?.({ description: editedDescription })
    }
    setIsEditingDescription(false)
  }, [editedDescription, card.description, onUpdate])

  if (!open) return null

  const hasError = card.error && (card.column === 'failed' || card.column === 'verifying' || card.column === 'needs_review')

  return (
    <div
      data-testid="card-detail-modal"
      className="fixed inset-0 z-50 flex items-start justify-center pt-16 bg-black/50 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-2xl mx-4 mb-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-4 border-b border-gray-800">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-100">{card.title}</h2>
            <div className="flex items-center gap-2 mt-1.5">
              <span
                data-testid="card-status"
                className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded"
              >
                {STATUS_LABELS[card.column] ?? card.column}
              </span>
              <span
                data-testid="card-priority"
                className={`text-xs px-2 py-0.5 rounded border ${PRIORITY_COLORS[card.priority]}`}
              >
                {card.priority}
              </span>
            </div>
          </div>
          <button
            data-testid="close-modal"
            className="text-gray-500 hover:text-gray-300 transition-colors text-xl leading-none"
            onClick={onClose}
          >
            x
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-medium text-gray-400">Description</h3>
              {!isEditingDescription && (
                <button
                  data-testid="edit-description"
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  onClick={() => setIsEditingDescription(true)}
                >
                  Edit
                </button>
              )}
            </div>
            {isEditingDescription ? (
              <div>
                <textarea
                  ref={textareaRef}
                  className="w-full bg-gray-800 border border-gray-700 rounded text-sm text-gray-200 p-2 min-h-[80px] focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                />
                <div className="flex gap-2 mt-1.5 justify-end">
                  <button
                    className="text-xs text-gray-400 hover:text-gray-200 px-2 py-1"
                    onClick={() => {
                      setEditedDescription(card.description)
                      setIsEditingDescription(false)
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded transition-colors"
                    onClick={handleSaveDescription}
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-300">
                {card.description || 'No description'}
              </p>
            )}
          </div>

          {/* Status dropdown */}
          <div>
            <h3 className="text-sm font-medium text-gray-400 mb-1">Status</h3>
            <select
              data-testid="status-select"
              className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={card.column}
              onChange={(e) => onStatusChange?.(e.target.value)}
            >
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-xs text-gray-500">Source</span>
              <p className="text-sm text-gray-300">
                {SOURCE_LABELS[card.source] ?? card.source}
              </p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Created</span>
              <p className="text-sm text-gray-300">{formatTimestamp(card.createdAt)}</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Updated</span>
              <p className="text-sm text-gray-300">{formatTimestamp(card.updatedAt)}</p>
            </div>
            {card.completedAt && (
              <div>
                <span className="text-xs text-gray-500">Completed</span>
                <p className="text-sm text-gray-300">{formatTimestamp(card.completedAt)}</p>
              </div>
            )}
            {card.targetHosts.length > 0 && (
              <div>
                <span className="text-xs text-gray-500">Target Hosts</span>
                <p className="text-sm text-gray-300">{card.targetHosts.join(', ')}</p>
              </div>
            )}
          </div>

          {/* Error details */}
          {hasError && (
            <div data-testid="error-details" className="bg-red-900/20 border border-red-800/30 rounded p-3">
              <h3 className="text-sm font-medium text-red-400 mb-1">Error</h3>
              <pre className="text-xs text-red-300 whitespace-pre-wrap font-mono">
                {card.error}
              </pre>
            </div>
          )}

          {/* Execution Timeline */}
          {card.events && card.events.length > 0 && (
            <div data-testid="execution-timeline">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Execution Timeline</h3>
              <div className="space-y-2 border-l-2 border-gray-800 pl-4 ml-1">
                {card.events.map((event) => (
                  <div key={event.id} className="relative">
                    <div className="absolute -left-[1.35rem] top-1 w-2 h-2 rounded-full bg-gray-600" />
                    <div className="text-xs text-gray-500 mb-0.5">
                      {formatTimestamp(event.createdAt)}
                    </div>
                    <div className="text-sm text-gray-300">
                      {event.tool ? `${event.eventType}: ${event.tool}` : event.eventType}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
